import {
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipDirective } from '../../../shared/components/ui/tooltip/tooltip.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Clipboard } from '@angular/cdk/clipboard';
import { FaIconComponent, FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faLock,
  faKey,
  faArrowsRotate,
  faPlus,
  faPenToSquare,
  faLink,
  faBolt,
  faClock,
  faPeopleGroup,
} from '@fortawesome/pro-duotone-svg-icons';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { OdinConnectionStatus, OdinService } from '../../../core/services/odin.service';
import { AppService } from '../../../core/services/app.service';
import { UserSettingsStore } from '../../../state/user-settings.repository';
import { AppStore, setDefaultSettingsTab } from '../../../state/app.repository';
import { AppConfigStore } from '../../../state/app-config.repository';
import { PermissionStore } from '../../../state/permissions.repository';
import { Room, RoomsStore } from '../../../state/rooms.repository';
import { Button } from '../../../shared/components/ui/buttons/gradient-btn/button';
import { TextField } from '../../../shared/components/form-fields/text-field/text-field';
import { Fieldset } from '../../../shared/components/ui/fieldset/fieldset';
import { SubmitDirective } from '../../../shared/directives/submit.directive';
import { E2eEncryptionPanel } from '../e2e-encryption-panel/e2e-encryption-panel';
import { AutocompleteField } from '../../../shared/components/form-fields/autocomplete-field/autocomplete-field';
import { TextTickerDirective } from '../../../shared/directives/text-ticker.directive';
import { ScheduleDisplay } from '../../../shared/components/ui/schedule-display/schedule-display';
import { JoinApprovalOverlay } from '../join-approval-overlay/join-approval-overlay';
import { RoomPlan } from '../room-plan/room-plan';
import { TextFieldAction } from '../../../shared/components/form-fields/text-field/text-field.types';
import { generateRoomName, RoomDialogService } from '../../rooms';
import { normalizeRoomId } from '../../../shared/utils/room-id';
import { RoomPasswordStore } from '../../../state/room-passwords.repository';
import { pickPlanRooms } from './room-plan-picks';
import { AppUpdateNotice } from '../app-update-notice/app-update-notice';
import { TrialNotice } from '../trial-notice/trial-notice';
import { TrialService } from '../../../core/services/trial.service';
import { LanguageService } from '../../../core/services/language.service';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';

/**
 * Sentinel `roomId` for the fixed "new instant meeting" entry that leads the
 * room autocomplete. It can never collide with a real room id (those are
 * server-minted UUIDs). Selecting this entry switches the primary action from
 * "Join Room" to "Create instant meeting".
 */
export const INSTANT_MEETING_ID = '__new_instant_meeting__';

@Component({
  selector: 'andor-room-login',
  standalone: true,
  imports: [
    AppUpdateNotice,
    TrialNotice,
    CommonModule,
    TooltipDirective,
    MatProgressSpinnerModule,
    FaIconComponent,
    FaDuotoneIconComponent,
    TranslatePipe,
    ReactiveFormsModule,
    Button,
    TextField,
    Fieldset,
    SubmitDirective,
    E2eEncryptionPanel,
    AutocompleteField,
    ScheduleDisplay,
    JoinApprovalOverlay,
    RoomPlan,
    TextTickerDirective,
  ],
  templateUrl: './room-login.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './room-login.scss',
})
export class RoomLogin {
  private readonly _rooms = inject(RoomsStore);
  private readonly _permissions = inject(PermissionStore);
  private readonly _translate = inject(TranslateService);
  // Read before `instant()` so labels re-translate when the language changes.
  private readonly _lang = inject(LanguageService).active;
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _clipboard = inject(Clipboard);
  private readonly _roomDialog = inject(RoomDialogService);
  private readonly _roomPasswords = inject(RoomPasswordStore);
  /** An expired trial admits nobody (the server refuses too). */
  protected readonly trial = inject(TrialService);

  readonly userNameControl = new FormControl('', [
    Validators.required,
    Validators.minLength(3),
    Validators.pattern(/^[A-Za-z].*$/),
  ]);

  // --- JOIN ---------------------------------------------------------------
  // Current search text + the selected room's id (server-minted).
  readonly roomQuery = signal('');
  readonly selectedRoomId = signal('');
  // Whether the fixed "new instant meeting" entry is the current selection.
  // Drives the primary button's label/behaviour (create-and-join vs join).
  readonly instantMeetingSelected = signal(false);
  // Bound value of the autocomplete (the picked Room). Lets us write the
  // last-joined room back into the input as a prefill (see constructor).
  readonly selectedRoomControl = new FormControl<Room | null>(null);

  // Exposed for the option template to single out the instant-meeting row.
  protected readonly instantMeetingId = INSTANT_MEETING_ID;
  // The fixed first autocomplete entry. A non-room "action" row shaped as a
  // Room so it rides the same list; identified by its sentinel id.
  private readonly _instantEntry: Room = {
    roomId: INSTANT_MEETING_ID,
    // Shown translated by the option template and `displayRoom`.
    name: _('New instant meeting'),
    isPrivate: false,
    hasPassword: false,
    isHidden: false,
    memberCount: 0,
  };

  private readonly _rooms$ = toSignal(this._rooms.rooms$, { initialValue: [] as Room[] });
  readonly filteredRooms = computed<Room[]>(() => {
    const q = this.roomQuery().trim().toLowerCase();
    // Hidden rooms (e.g. instant meetings resolved via an invite link) are
    // never offered in the browse list — they are reachable only by their id.
    const rooms = this._rooms$().filter((r) => !r.isHidden);
    const matched = !q
      ? rooms
      : // Filter by display name; also allow an exact (pasted) roomId match.
        rooms.filter((r) => r.name.toLowerCase().includes(q) || r.roomId.toLowerCase() === q);
    // Lead with the fixed "new instant meeting" action for room managers — only
    // they may open a new room. Typed names still reach existing hidden rooms;
    // for anyone else the server answers ROOM_NOT_FOUND for a missing one.
    return this.canManageRooms() ? [this._instantEntry, ...matched] : matched;
  });

  // The room whose e2e password the join view asks for (the joiner gate —
  // never the creator toggle), or '' for none: the joined room — picked, or a
  // typed id matching a listed one — when it is listed as password-protected,
  // or any typed room id/name the server already refused for its password,
  // as hidden rooms are never listed. The typed case is keyed by the slug
  // `_openRoomByName` connects with.
  readonly passwordRoomId = computed<string>(() => {
    if (this.instantMeetingSelected()) return '';
    const id = this._resolveRoomId() || (normalizeRoomId(this.roomQuery().trim()) ?? '');
    if (!id) return '';
    const listed = this._rooms$().find((r) => r.roomId === id)?.hasPassword;
    return listed || this._roomPasswords.requiresPassword(id) ? id : '';
  });

  // The selected room, or null when nothing (or the instant-meeting action,
  // which carries no roomId) is picked. Drives the copy + edit affordances.
  private readonly _selectedRoom = computed<Room | null>(() => {
    const id = this.selectedRoomId();
    if (!id) return null;
    return this._rooms$().find((r) => r.roomId === id) ?? null;
  });
  readonly hasSelectedRoom = computed(() => this._selectedRoom() !== null);

  // The selected room's schedule, if it has one (drives the schedule row).
  readonly selectedRoomSchedule = computed(() => {
    const id = this.selectedRoomId();
    return id ? this._rooms$().find((r) => r.roomId === id)?.schedule : undefined;
  });

  // Coarse clock so the plan's schedule bucket rolls over as occurrences
  // start/end — same cadence as the tiles' door-open state.
  private readonly _now = signal(Date.now());
  // The fixed-size building-plan preview: imminent meeting first, then rooms
  // with users, then remaining scheduled rooms, then leftovers (see
  // `pickPlanRooms`).
  readonly planPicks = computed(() => pickPlanRooms(this._rooms$(), this._now()));
  readonly planRooms = computed(() => this.planPicks().rooms);
  // Schedule row under the plan: only the selected room's schedule, and only
  // when it has one — nothing is shown until the user picks a room.
  readonly displayedSchedule = computed(() => this.selectedRoomSchedule() ?? null);
  // Whether the plan preview cuts rooms off — gates the "see all rooms" jump
  // to the full building plan.
  readonly hasMoreRooms = computed(() => {
    const picks = this.planPicks();
    return picks.totalVisible > picks.rooms.length;
  });
  // Toggles the full building-plan view (all rooms) in place of the login
  // form. Kept as local view state — not an app section — so a tile-click
  // flows straight back into this component's autocomplete selection.
  readonly showFullPlan = signal(false);

  // --- CREATE -------------------------------------------------------------
  // Gates the "+" create button; the create form itself lives in a dialog.
  readonly canManageRooms = toSignal(this._permissions.manageRooms$, { initialValue: false });

  developerMode = signal<boolean>(false);

  // The join-view e2e password panel (only rendered while `passwordRoomId`
  // names a room).
  private readonly _encryption = viewChild(E2eEncryptionPanel);

  protected readonly faLock = faLock;
  protected readonly faKey = faKey;
  protected readonly faPlus = faPlus;
  protected readonly faPenToSquare = faPenToSquare;
  protected readonly faLink = faLink;
  protected readonly faBolt = faBolt;
  protected readonly faClock = faClock;
  protected readonly faPeopleGroup = faPeopleGroup;

  // Refresh control rendered as an inline action inside the room search input
  // (right side) rather than as a separate adjacent button.
  protected readonly roomSearchActions = computed<TextFieldAction[]>(() => {
    this._lang();
    const label = this._translate.instant('Refresh room list');
    return [
      { icon: faArrowsRotate, tooltip: label, ariaLabel: label, click: () => this.refresh() },
    ];
  });

  protected readonly OdinConnectionStatus = OdinConnectionStatus;

  constructor(
    public userSettingsStore: UserSettingsStore,
    public odin: OdinService,
    public appService: AppService,
    public appStore: AppStore,
    public appConfigStore: AppConfigStore
  ) {
    this.userSettingsStore.userName$.pipe(takeUntilDestroyed()).subscribe((userName) => {
      this.userNameControl.patchValue(userName, { emitEvent: false });
    });
    this.appConfigStore.developerMode$.pipe(takeUntilDestroyed()).subscribe((developerMode) => {
      this.developerMode.set(developerMode);
    });

    const nowInterval = setInterval(() => this._now.set(Date.now()), 30_000);
    inject(DestroyRef).onDestroy(() => clearInterval(nowInterval));

    // Prefill the search box with the last-joined room (persisted roomId), but
    // only once the room list has loaded and only if that room still exists —
    // a stale id whose room was deleted/renamed server-side prefills nothing.
    this._rooms.rooms$
      .pipe(
        filter((rooms) => rooms.length > 0),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe((rooms) => {
        // Don't clobber a selection or query the user has already started.
        if (this.selectedRoomId() || this.roomQuery()) return;
        const lastId = this.userSettingsStore.value.roomId;
        if (!lastId) return;
        const room = rooms.find((r) => r.roomId === lastId);
        if (!room) return;
        this.selectedRoomControl.setValue(room);
        this.selectedRoomId.set(room.roomId);
      });
  }

  onRoomQuery(query: string) {
    this.roomQuery.set(query);
    // Typing invalidates a prior selection (incl. the instant-meeting action).
    this.selectedRoomId.set('');
    this.instantMeetingSelected.set(false);
  }

  onRoomPicked(room: Room) {
    if (room.roomId === INSTANT_MEETING_ID) {
      // The "new instant meeting" action: no concrete room to join yet.
      this.instantMeetingSelected.set(true);
      this.selectedRoomId.set('');
      return;
    }
    this.instantMeetingSelected.set(false);
    this.selectedRoomId.set(room.roomId);
  }

  // A tile picked on the building plan: same effect as picking the room from
  // the autocomplete, plus the input is auto-filled with the room's name.
  onPlanRoomSelected(room: Room) {
    this.instantMeetingSelected.set(false);
    this.selectedRoomId.set(room.roomId);
    this.selectedRoomControl.setValue(room);
  }

  // How a room renders as the input text / its stable key for the autocomplete.
  displayRoom = (room: Room): string =>
    room.roomId === INSTANT_MEETING_ID ? this._translate.instant(room.name) : room.name;
  roomKey = (room: Room): string => room.roomId;

  // The effective roomId to join: an explicit selection, or a pasted/typed id
  // that matches a known room.
  private _resolveRoomId(): string {
    if (this.selectedRoomId()) return this.selectedRoomId();
    const typed = this.roomQuery().trim();
    const match = this._rooms$().find((r) => r.roomId === typed);
    return match ? match.roomId : '';
  }

  // Returns the in-flight promise so the inline refresh action can show its
  // loading spinner and a brief success/error checkmark once the list updates.
  refresh(): Promise<void> {
    return this.appService.refreshRooms();
  }

  openDevSettings() {
    setDefaultSettingsTab('developer');
    this.appStore.update({ activeSection: 'general-settings' });
  }

  // Reveal the full building plan as an in-place view over the login form.
  openFullPlan() {
    this.showFullPlan.set(true);
  }

  // A tile picked on the full building plan: select it (as if picked from the
  // autocomplete) and drop back to the login form.
  onFullPlanRoomSelected(room: Room) {
    this.onPlanRoomSelected(room);
    this.showFullPlan.set(false);
  }

  async join(enforceWebrtcTransport?: boolean) {
    const userName = this.userNameControl.value;
    if (!userName || this.userNameControl.invalid) return;

    // Instant shortcut: open a fresh, end-to-end encrypted room with a
    // generated name and password (shared through the in-call invite link).
    if (this.instantMeetingSelected()) {
      const roomName = await this._freeInstantRoomName();
      const roomId = normalizeRoomId(roomName);
      if (roomId) this._roomPasswords.set(roomId, this._roomPasswords.generate());
      await this._openRoomByName(roomName, enforceWebrtcTransport);
      return;
    }

    // An explicit selection or a pasted id that matches a known room → join it
    // as-is (honouring its stored password gate).
    const existingId = this._resolveRoomId();
    if (existingId) {
      const room = this._rooms$().find((r) => r.roomId === existingId);
      this.userSettingsStore.update({ userName, roomId: existingId, roomName: room?.name ?? '' });
      const encryption = this._encryption();
      if (encryption && !encryption.commitForJoin(existingId)) return;
      const success = await this.odin.connect(existingId, enforceWebrtcTransport);
      if (success) this.appService.wasInvited = false;
      return;
    }

    // Otherwise the typed text is a NEW room name → open a room by that name —
    // after committing its password when the server already asked for one.
    const typedName = this.roomQuery().trim();
    if (!typedName) return;
    const passwordRoomId = this.passwordRoomId();
    const encryption = this._encryption();
    if (passwordRoomId && encryption && !encryption.commitForJoin(passwordRoomId)) return;
    await this._openRoomByName(typedName, enforceWebrtcTransport);
  }

  // Open (or rendezvous into) a hidden instant room BY NAME. The server slugifies
  // the name into the shareable id, creating the room on first open and joining
  // any later opener with the same name/link into the same room. End-to-end
  // encrypted only when we hold a password for it: the instant-meeting
  // shortcut's generated one, or an invite link's `#p=` fragment; a typed name
  // opens an unencrypted room. The room is deleted server-side once its last
  // member leaves.
  //
  // NOTE: when the typed name slugifies to no existing id but matches a LISTED
  // room's name (server-side `findRoomByNameSlug` in the join endpoint), the
  // user joins THAT room instead of opening a same-named shadow instant room
  // — `connect()` adopts the redirected roomId from the response. Deliberate:
  // two rooms sharing a display name would be indistinguishable in the UI.
  private async _openRoomByName(roomName: string, enforceWebrtcTransport?: boolean) {
    const userName = this.userNameControl.value;
    if (!userName || this.userNameControl.invalid) return;
    const roomId = normalizeRoomId(roomName);
    if (!roomId) return;
    this.userSettingsStore.update({ userName, roomId, roomName });
    const success = await this.odin.connect(roomId, enforceWebrtcTransport, false, {
      createIfMissing: true,
      roomName,
    });
    if (success) this.appService.wasInvited = false;
  }

  /**
   * A generated room name no room answers to yet, so the instant room is new
   * and its generated password applies (an existing room keeps its own E2EE
   * mode). Checks the exact id (hidden rooms included) and listed rooms'
   * names, which a join also matches. After a few taken names it settles for
   * the last one: the join then lands in that room, and `OdinService` drops a
   * password the room does not have.
   */
  private async _freeInstantRoomName(): Promise<string> {
    let name = generateRoomName();
    for (let attempt = 0; attempt < 5; attempt++) {
      const id = normalizeRoomId(name);
      const taken =
        !id ||
        this._rooms$().some((room) => normalizeRoomId(room.name) === id) ||
        (await this.appService.lookupRoom(id)) !== null;
      if (!taken) return name;
      name = generateRoomName();
    }
    return name;
  }

  // Opens the create-room dialog. Creating only opens the room server-side
  // (no join); the new room is then surfaced in the join list below: refresh
  // the list and pre-select the new room in the search field so the user can
  // join it later in one click. The dialog confirms the creation itself (invite
  // link + password), so nothing is toasted here.
  async openCreateDialog() {
    if (!this.canManageRooms()) return;
    const created = await this._roomDialog.promptCreateRoom();
    if (!created) return;

    await this.appService.refreshRooms();
    const room = this._rooms$().find((r) => r.roomId === created.roomId);
    if (room) {
      this.selectedRoomControl.setValue(room);
      this.selectedRoomId.set(room.roomId);
      this.roomQuery.set('');
    }
  }

  // Copies the selected room's invite link — available to everyone, since
  // sharing a room is not a moderator action. The link carries the room's e2e
  // password as a `#p=` fragment when this device holds it.
  copyInviteLink() {
    const room = this._selectedRoom();
    if (!room) return;
    this._clipboard.copy(this.appService.buildInviteLink(room.roomId));
    this._snackBar.open(
      this._translate.instant('A room invite link has been copied to your clipboard.'),
      undefined,
      { duration: 3000, horizontalPosition: 'center', verticalPosition: 'top' }
    );
  }

  // Opens the room dialog on the currently selected room. The updated room is
  // already in `RoomsStore` when this resolves, but the autocomplete holds the
  // room object it was given, so write the fresh one back — otherwise a rename
  // would leave the stale name in the input.
  async openEditDialog() {
    if (!this.canManageRooms()) return;
    const room = this._selectedRoom();
    if (!room) return;

    const updated = await this._roomDialog.promptEditRoom(room);
    if (updated) this.selectedRoomControl.setValue(updated);
  }

  retry(alwaysEnforceWebrtcTransport?: boolean) {
    if (alwaysEnforceWebrtcTransport) {
      this.userSettingsStore.update({ useWebRtc: true });
    }
    // The room was already selected/created; retry by joining its id.
    const roomId = this.userSettingsStore.value.roomId;
    if (roomId) this.odin.connect(roomId, true);
  }

  cancel() {
    this.odin.disconnect().then();
    this.odin.resetConnectionError();
  }

  isJoinDisabled(): boolean {
    if (this.trial.expired()) return true;
    if (this.userNameControl.invalid) return true;
    if (this.appService.authenticationState() === 'loading') return true;
    // The instant-meeting action needs only a valid display name — there is no
    // room to select and no joiner password gate.
    if (this.instantMeetingSelected()) return false;
    // Joining a known room, or a typed one the server asked a password for:
    // honour its password gate.
    if (this._resolveRoomId() || this.passwordRoomId()) {
      return this._encryption()?.joinBlocked() ?? false;
    }
    // No known match: enabled iff the typed text yields a usable room name/slug
    // (open-a-room-by-name). All-punctuation text that slugs to nothing stays
    // disabled.
    const typed = this.roomQuery().trim();
    return typed.length === 0 || normalizeRoomId(typed) === null;
  }
}
